import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Users, Pencil, Trash2, Wrench, Phone, Mail, ChevronRight, Home, MapPinCheck, MapPinOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/EmptyState";
import { CustomerDialog } from "@/components/CustomerDialog";
import { TypeBadge } from "@/components/Badges";
import { PageHeader } from "@/components/PageHeader";
import { formatOrgNumber } from "@/components/geo/BrregSearch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/** Liten merkelapp for privatkunde + kart-indikator (har koordinatar / manglar). */
const EntityHints = ({ c }) => (
  <span className="inline-flex items-center gap-1.5 align-middle">
    {c.entity_type === "privat" && (
      <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-1.5 py-px text-[10px] font-semibold text-muted-foreground" data-testid={`customer-entity-badge-${c.id}`}>
        <Home className="h-3 w-3" /> Privat
      </span>
    )}
    {c.address && (c.lat != null
      ? <MapPinCheck className="h-3.5 w-3.5 text-[rgb(var(--status-ok))]" aria-label="På kartet" title="Har koordinatar – vist på kartet" data-testid={`customer-geo-ok-${c.id}`} />
      : <MapPinOff className="h-3.5 w-3.5 text-muted-foreground/60" aria-label="Manglar koordinatar" title="Adressa mangla koordinatar – presiser adressa for å få kunden på kartet" data-testid={`customer-geo-missing-${c.id}`} />)}
  </span>
);

const subline = (c) => [c.org_number ? `Org.nr ${formatOrgNumber(c.org_number)}` : null, c.department, c.address].filter(Boolean).join(" · ");

export default function Kunder() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    if (params.get("ny") === "1") {
      setEditing(null);
      setDialogOpen(true);
      const p = new URLSearchParams(params);
      p.delete("ny");
      setParams(p, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase().trim();
    return customers.filter((c) => {
      if (type !== "all" && (c.customer_type || "engangskunde") !== type) return false;
      if (!t) return true;
      return [c.name, c.contact_person, c.phone, c.email, c.department, c.address, c.org_number, c.city].filter(Boolean).join(" ").toLowerCase().includes(t);
    });
  }, [customers, q, type]);

  const counts = useMemo(() => ({
    all: customers.length,
    serviceavtale: customers.filter((c) => c.customer_type === "serviceavtale").length,
    engangskunde: customers.filter((c) => (c.customer_type || "engangskunde") === "engangskunde").length,
  }), [customers]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const doDelete = async () => {
    try {
      await api.delete(`/customers/${toDelete.id}`);
      toast.success("Kunde sletta.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje slette.");
      setToDelete(null);
    }
  };

  const chip = "h-9 shrink-0 gap-1.5 rounded-full border px-3 text-xs data-[state=on]:border-transparent data-[state=on]:bg-[#0B0D10] data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-[#0B0D10]";

  return (
    <div className="space-y-5">
      <PageHeader
        title="Kundar"
        description="Firma, kontaktpersonar og kundetype. Klikk på maskin-talet for å sjå maskinparken."
        actions={<Button onClick={openNew} data-testid="new-customer-button"><Plus /> Ny kunde</Button>}
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customer-search-input" placeholder="Søk kunde, kontakt, telefon …" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-card pl-9" />
        </div>
        <ToggleGroup type="single" value={type} onValueChange={(v) => v && setType(v)} className="justify-start gap-1.5" data-testid="customer-type-filter">
          <ToggleGroupItem value="all" className={chip} data-testid="customer-filter-all">Alle <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.all}</span></ToggleGroupItem>
          <ToggleGroupItem value="serviceavtale" className={chip} data-testid="customer-filter-serviceavtale">Serviceavtale <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.serviceavtale}</span></ToggleGroupItem>
          <ToggleGroupItem value="engangskunde" className={chip} data-testid="customer-filter-engangskunde">Engangskunde <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.engangskunde}</span></ToggleGroupItem>
        </ToggleGroup>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-sm">
          <EmptyState className="m-4 border-0" icon={Users} title={customers.length === 0 ? "Ingen kundar" : "Ingen treff"}
            description={customers.length === 0 ? "Legg til den første kunden for å knyte maskiner til." : "Prøv eit anna søk eller filter."}
            action={customers.length === 0 && <Button onClick={openNew}><Plus /> Ny kunde</Button>} />
        </Card>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden" data-testid="customer-card-list">
            {filtered.map((c) => (
              <div key={c.id} className="rounded-lg border bg-card p-3 shadow-sm" data-testid={`customer-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5"><span className="truncate font-semibold">{c.name}</span> <EntityHints c={c} /></div>
                    {c.contact_person && <div className="truncate text-xs text-muted-foreground">{c.contact_person}{c.department ? ` · ${c.department}` : ""}</div>}
                    {c.address && <div className="truncate text-xs text-muted-foreground">{c.address}</div>}
                  </div>
                  <TypeBadge type={c.customer_type} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3.5 w-3.5" /> {c.phone}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3.5 w-3.5" /> {c.email}</a>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <button type="button" onClick={() => navigate(`/maskiner?customer=${c.id}`)} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-muted px-3 text-xs font-semibold hover:bg-accent" data-testid={`customer-machines-${c.id}`}>
                    <Wrench className="h-3.5 w-3.5" /> {c.machine_count ?? 0} maskiner <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <div className="flex gap-1">
                    <Button size="iconSm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                    {isAdmin && <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(c)} data-testid={`delete-customer-${c.id}`} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <Card className="hidden overflow-hidden shadow-sm md:block">
            <Table data-testid="customer-list-table">
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Kontakt</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>E-post</TableHead>
                  <TableHead className="text-right">Maskiner</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="row-hover">
                    <TableCell>
                      <div className="flex items-center gap-2 font-semibold">{c.name} <EntityHints c={c} /></div>
                      {subline(c) && <div className="text-xs text-muted-foreground">{subline(c)}</div>}
                    </TableCell>
                    <TableCell><TypeBadge type={c.customer_type} /></TableCell>
                    <TableCell className="text-muted-foreground">{c.contact_person || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{c.phone ? <a href={`tel:${c.phone}`} className="hover:text-foreground hover:underline">{c.phone}</a> : "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{c.email ? <a href={`mailto:${c.email}`} className="hover:text-foreground hover:underline">{c.email}</a> : "—"}</TableCell>
                    <TableCell className="text-right">
                      <button type="button" onClick={() => navigate(`/maskiner?customer=${c.id}`)} className="inline-flex h-8 items-center gap-1 rounded-full bg-muted px-2.5 text-xs font-semibold hover:bg-accent" data-testid={`customer-machines-${c.id}`}>
                        <Wrench className="h-3.5 w-3.5" /> {c.machine_count ?? 0}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="iconSm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                        {isAdmin && <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(c)} data-testid={`delete-customer-${c.id}`} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <CustomerDialog open={dialogOpen} onOpenChange={setDialogOpen} customer={editing} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette kunde?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} blir sletta. Kundar med tilknytte maskiner kan ikkje slettast.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-customer">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
